import React, { useRef } from 'react';
import { Button } from '@/components/ui/button';
import { Card, CardContent } from '@/components/ui/card';
import { Upload, X, Eye, Download, Loader2 } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { useSignedUrl } from '@/hooks/useSignedUrl';
interface FileUploadProps {
  title: string;
  description: string;
  currentFileKey: string | null;
  onFileUpload: (file: File) => Promise<void>;
  onFileRemove: () => Promise<void>;
  acceptedFormats: string;
  maxSize: string;
  isUploading?: boolean;
  isRemoving?: boolean;
  fileType: 'image' | 'document';
}
const FileUpload: React.FC<FileUploadProps> = ({
  title,
  description,
  currentFileKey,
  onFileUpload,
  onFileRemove,
  acceptedFormats,
  maxSize,
  isUploading = false,
  isRemoving = false,
  fileType
}) => {
  const {
    t
  } = useTranslation();
  const fileInputRef = useRef<HTMLInputElement>(null);
  const bucket = fileType === 'image' ? 'gym-logos' : 'gym-documents';
  const {
    signedUrl,
    isLoading: isLoadingUrl
  } = useSignedUrl(currentFileKey, bucket);
  const handleFileSelect = (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (file) {
      onFileUpload(file);
    }
    // Reset input value to allow re-uploading the same file
    if (fileInputRef.current) {
      fileInputRef.current.value = '';
    }
  };
  const handleUploadClick = () => {
    fileInputRef.current?.click();
  };
  const handleViewFile = () => {
    if (signedUrl) {
      window.open(signedUrl, '_blank');
    }
  };
  const handleDownloadFile = () => {
    if (signedUrl && currentFileKey) {
      const link = document.createElement('a');
      link.href = signedUrl;
      link.download = currentFileKey;
      link.click();
    }
  };
  return <Card>
      <CardContent className="p-6">
        <div className="space-y-4">
          <div>
            <h3 className="text-lg font-medium greek-text">{title}</h3>
            <p className="text-sm text-muted-foreground greek-text">{description}</p>
            <p className="text-xs text-muted-foreground mt-1">{acceptedFormats} ({maxSize})</p>
          </div>

          {currentFileKey && <div className="space-y-3">
              {isLoadingUrl ? <div className="flex items-center justify-center p-3 bg-muted rounded-lg">
                  <Loader2 className="h-4 w-4 animate-spin mr-2" />
                  <span className="text-sm text-muted-foreground">Loading preview...</span>
                </div> : <div className="flex items-center justify-between p-3 bg-muted rounded-lg">
                  <div className="flex items-center gap-3">
                    {fileType === 'image' && signedUrl ? <img src={signedUrl} alt="Current file" className="w-12 h-12 rounded object-contain" /> : <div className="w-12 h-12 bg-primary/10 rounded flex items-center justify-center">
                        <Upload className="h-6 w-6 text-primary" />
                      </div>}
                    <div>
                      <p className="text-sm font-medium">
                        {fileType === 'image' ? t('gymSettings.currentLogo') : t('gymSettings.currentTerms')}
                      </p>
                      <p className="text-xs text-muted-foreground">
                        {currentFileKey}
                      </p>
                    </div>
                  </div>
                  
                  <div className="flex items-center gap-2">
                    <Button variant="outline" size="sm" onClick={handleViewFile} disabled={!signedUrl} className="h-8 w-8 p-0">
                      <Eye className="h-4 w-4" />
                    </Button>
                    {fileType === 'document' && <Button variant="outline" size="sm" onClick={handleDownloadFile} disabled={!signedUrl} className="h-8 w-8 p-0">
                        <Download className="h-4 w-4" />
                      </Button>}
                    <Button variant="outline" size="sm" onClick={onFileRemove} disabled={isRemoving} className="h-8 w-8 p-0 text-destructive hover:text-destructive">
                      <X className="h-4 w-4" />
                    </Button>
                  </div>
                </div>}
            </div>}

          <div className="flex gap-2">
            <input ref={fileInputRef} type="file" onChange={handleFileSelect} accept={fileType === 'image' ? 'image/*' : '.pdf,.doc,.docx'} className="hidden" />
            <Button onClick={handleUploadClick} disabled={isUploading} className="flex items-center gap-2">
              <Upload className="h-4 w-4" />
              {isUploading ? <span className="greek-text">Uploading...</span> : <span className="greek-text">
                  {currentFileKey ? fileType === 'image' ? t('gymSettings.changeLogo') : t('gymSettings.changeTerms') : fileType === 'image' ? t('gymSettings.uploadLogo') : t('gymSettings.uploadTerms')}
                </span>}
            </Button>
          </div>
        </div>
      </CardContent>
    </Card>;
};
export default FileUpload;